import './styles.scss';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  CheckboxGroup,
  Panel,
  Text,
  LabeledInput,
  PickerInput,
  LinkButton,
  Spinner,
  Tooltip,
} from '@epam/promo';
import { LazyDataSourceApiRequest, useLazyDataSource } from '@epam/uui';
import { ReactComponent as clearIcon } from '@epam/assets/icons/common/content-clear-12.svg';
import { useDictionary } from 'services/dictionary/dictionary-provider';
import ProjectListService from 'services/api/project-list/projectListService';
import { ISkilloTech } from 'modules/project-list/components/project-card/modal';
import {
  MAX_FILTER_EVENTS_COUNT,
  MAX_RESULTS_SKILLO_API,
  START_PAGE_NUMBER,
} from 'constants/uiConstants';
import { EventResponse } from 'modules/events/models/interfaces';
import eventManagementService from 'services/api/event-managment/eventManagementService';

export interface Props {
  onPositionsChange: (positions: string[]) => void;
  onTechnologiesChange: (technologies: string[]) => void;
  onEventsChange: (events: string[]) => void;
}

export const OpportunityFilter: FC<Props> = ({
  onPositionsChange,
  onTechnologiesChange,
  onEventsChange,
}) => {
  const { projectPositions } = useDictionary();
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<
    ISkilloTech[]
  >([]);
  const [selectedEvents, setSelectedEvents] = useState<EventResponse[]>([]);
  const [allPositions, setAllPositions] = useState<
    { name: string; id: string }[]
  >([]);
  const [eventsAreDisabled, setEventsAreDisabled] = useState<boolean>(true);

  const loadTechnologies = useCallback(
    async (request: LazyDataSourceApiRequest<ISkilloTech, string, unknown>) => {
      const query = request.search || 'A';
      const response = await ProjectListService.getSkilloTechsByQuery(
        query,
        MAX_RESULTS_SKILLO_API
      );
      return { items: response.results };
    },
    []
  );

  const loadEvents = useCallback(
    async (
      request: LazyDataSourceApiRequest<EventResponse, string, unknown>
    ) => {
      const query = request.search || '';
      const response = await eventManagementService.getEvents(
        MAX_FILTER_EVENTS_COUNT /*amount*/,
        START_PAGE_NUMBER /*offset*/,
        query,
        false,
        true
      );

      return { items: response.challengeList };
    },
    [eventManagementService, MAX_FILTER_EVENTS_COUNT, START_PAGE_NUMBER]
  );

  useEffect(() => {
    loadEvents({}).then((events) => {
      setEventsAreDisabled(events.items.length === 0);
    });
  }, [loadEvents, setEventsAreDisabled]);

  const allTechnologies = useLazyDataSource(
    { api: loadTechnologies, selectAll: false },
    []
  );

  const allEventsDataSource = useLazyDataSource(
    { api: loadEvents, selectAll: false },
    [loadEvents]
  );

  useEffect(() => {
    setAllPositions(
      projectPositions?.items.map((p) => ({ name: p.label, id: p.value })) ?? []
    );
  }, [projectPositions]);

  useEffect(() => {
    onPositionsChange(selectedPositions);
  }, [selectedPositions]);

  useEffect(() => {
    onTechnologiesChange(selectedTechnologies.map((item) => item.name));
  }, [selectedTechnologies]);

  useEffect(() => {
    onEventsChange(selectedEvents.map((item) => item.name));
  }, [selectedEvents]);

  const clearFilter = () => {
    if (selectedPositions.length) {
      setSelectedPositions([]);
    }
    if (selectedTechnologies.length) {
      setSelectedTechnologies([]);
    }
    if (selectedEvents.length) {
      setSelectedEvents([]);
    }
  };

  return (
    <Panel cx="opportunity-filter" shadow background="white">
      <div className="opportunity-filter__header">
        <Text size="24" fontSize="16" font="sans-semibold">
          Filters
        </Text>
        <LinkButton
          caption="Clear all"
          onClick={clearFilter}
          icon={clearIcon}
          isDisabled={
            selectedPositions.length === 0 &&
            selectedTechnologies.length === 0 &&
            selectedEvents.length === 0
          }
          color="blue"
          size="24"
        />
      </div>
      <LabeledInput label="Position">
        {allPositions.length ? (
          <CheckboxGroup
            onValueChange={(checked) => setSelectedPositions(checked)}
            items={allPositions}
            value={selectedPositions}
            direction="vertical"
          />
        ) : (
          <Spinner color="blue" cx="loading-spinner" />
        )}
      </LabeledInput>
      <LabeledInput label="Skills" cx="opportunity-filter__technologies">
        <PickerInput
          dataSource={allTechnologies}
          value={selectedTechnologies}
          onValueChange={setSelectedTechnologies}
          emptyValue={[]}
          valueType="entity"
          selectionMode="multi"
          sorting={{ field: 'name', direction: 'asc' }}
        />
      </LabeledInput>

      <Tooltip
        placement="bottom"
        content={eventsAreDisabled && 'No ongoing events'}
      >
        <LabeledInput label="Events" cx="opportunity-filter__technologies">
          <PickerInput
            isDisabled={eventsAreDisabled}
            dataSource={allEventsDataSource}
            value={selectedEvents}
            onValueChange={setSelectedEvents}
            emptyValue={[]}
            valueType="entity"
            selectionMode="multi"
            sorting={{ field: 'name', direction: 'asc' }}
          />
        </LabeledInput>
      </Tooltip>
    </Panel>
  );
};
